import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ArrowLeft, Briefcase, Eye, Users, Plus, Edit2, XCircle, CheckCircle, Calendar, Trash2, Upload } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { motion } from 'motion/react';
import { triggerAndroidHaptic } from '../utils/haptics';
import { PageShell } from './layout/PageShell';

interface MyGigsPageProps {
  accessToken: string;
  isVisible?: boolean;
  userType: 'talent' | 'employer';
  onBack: () => void;
  onViewInterests: (gigId: string, gigTitle: string) => void;
  onEditGig: (gigId: string) => void;
  onCreateGig: () => void;
  /** When true (desktop modal), hide the sub-page header; modal chrome shows back button. */
  hideHeader?: boolean;
}

export function MyGigsPage({
  accessToken,
  isVisible = true,
  userType,
  onBack,
  onViewInterests,
  onEditGig,
  onCreateGig,
  hideHeader = false,
}: MyGigsPageProps) {
  const [gigs, setGigs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [closingGigId, setClosingGigId] = useState<string | null>(null);
  const [publishingGigId, setPublishingGigId] = useState<string | null>(null);
  const [deletingGigId, setDeletingGigId] = useState<string | null>(null);
  const [confirmDeleteGigId, setConfirmDeleteGigId] = useState<string | null>(null);

  // iOS PWA: JS-based sticky sub-page header (position:sticky broken in standalone)
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const stickyPlaceholderRef = useRef<HTMLDivElement>(null);
  const subPageContentRef = useRef<HTMLDivElement>(null);

  // iOS PWA: when page becomes visible, scroll .sub-page-content to top (persistent mount keeps scroll position)
  useEffect(() => {
    if (!isVisible) return;
    const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const el = subPageContentRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTo(0, 0);
    });
    return () => cancelAnimationFrame(id);
  }, [isVisible]);

  useEffect(() => {
    const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const sentinel = stickySentinelRef.current;
    const bar = stickyHeaderRef.current;
    const placeholder = stickyPlaceholderRef.current;
    if (!sentinel || !bar || !placeholder) return;
    const header = document.querySelector('.pwa-nav-top');
    const headerH = header ? header.getBoundingClientRect().height : 56;
    let isStuck = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldStick = !entry.isIntersecting;
        if (shouldStick === isStuck) return;
        isStuck = shouldStick;
        if (shouldStick) {
          const mb = parseFloat(window.getComputedStyle(bar).marginBottom) || 0;
          placeholder.style.height = `${bar.offsetHeight + mb}px`;
          bar.classList.add('is-stuck');
        } else {
          bar.classList.remove('is-stuck');
          placeholder.style.height = '0px';
        }
      },
      { rootMargin: `-${Math.ceil(headerH)}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Refetch when accessToken changes (e.g. after re-login) so list is not empty
  useEffect(() => {
    if (!accessToken) return;
    if (isVisible) {
      fetchMyGigs();
    }
  }, [accessToken, isVisible]);

  useEffect(() => {
    const handleRefresh = () => {
      if (!accessToken || !isVisible) return;
      fetchMyGigs();
    };

    window.addEventListener('talentfunn:gigs-refresh', handleRefresh);
    window.addEventListener('talentfunn:gig-interests-refresh', handleRefresh);
    return () => {
      window.removeEventListener('talentfunn:gigs-refresh', handleRefresh);
      window.removeEventListener('talentfunn:gig-interests-refresh', handleRefresh);
    };
  }, [accessToken, isVisible]);

  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (!isVisible || !accessToken) return;
      if (document.visibilityState === 'visible') {
        fetchMyGigs();
      }
    };

    const handleOnline = () => {
      if (!isVisible || !accessToken) return;
      fetchMyGigs();
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, [accessToken, isVisible]);

  const fetchMyGigs = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/my-gigs`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.error || 'Kunne ikke hente oppdrag');
        return;
      }

      const data = await response.json();
      setGigs(data.gigs || []);
    } catch (error) {
      toast.error('Kunne ikke hente oppdrag');
    } finally {
      setLoading(false);
    }
  };

  const handleUnpublishGig = async (gigId: string) => {
    setClosingGigId(gigId);
    try {
      const response = await fetch(
        `${API_BASE}/gigs/${gigId}/close`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.error || 'Kunne ikke avpublisere oppdraget');
        return;
      }

      toast.success('Oppdraget er tatt ned fra søk');
      fetchMyGigs();
    } catch (error) {
      toast.error('Kunne ikke avpublisere oppdraget');
    } finally {
      setClosingGigId(null);
    }
  };

  const handlePublishGig = async (gigId: string) => {
    setPublishingGigId(gigId);
    try {
      const response = await fetch(
        `${API_BASE}/gigs/${gigId}/publish`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.error || 'Kunne ikke publisere oppdraget');
        return;
      }

      toast.success('Oppdraget er publisert igjen');
      fetchMyGigs();
    } catch (error) {
      toast.error('Kunne ikke publisere oppdraget');
    } finally {
      setPublishingGigId(null);
    }
  };

  const handleDeleteGig = async (gigId: string) => {
    setDeletingGigId(gigId);
    try {
      const response = await fetch(
        `${API_BASE}/gigs/${gigId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.error || 'Kunne ikke slette oppdraget');
        return;
      }

      toast.success('Oppdraget er slettet');
      setConfirmDeleteGigId(null);
      fetchMyGigs();
    } catch (error) {
      toast.error('Kunne ikke slette oppdraget');
    } finally {
      setDeletingGigId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen xl:min-h-0 xl:h-full flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50 xl:overflow-hidden">
      {!hideHeader && (
        <>
          <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />
          <div ref={stickyHeaderRef} className="sub-page-header bg-white border-b border-gray-200 px-4 pt-4 pb-3 flex items-center gap-3 flex-shrink-0 sticky top-0 z-20">
            <button
              onClick={() => {
                triggerAndroidHaptic('light');
                onBack();
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-150 active:scale-95"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 flex items-center gap-3">
              <Briefcase className="w-8 h-8 text-purple-600 flex-shrink-0" />
              <div>
                <h2 className="font-semibold">Mine oppdrag</h2>
                <p className="text-sm text-gray-600">{gigs.length} oppdrag</p>
              </div>
            </div>
            <button
              onClick={() => {
                triggerAndroidHaptic('light');
                onCreateGig();
              }}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all duration-150 active:scale-[0.98] font-semibold flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nytt oppdrag</span>
            </button>
          </div>
          <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />
        </>
      )}

      {/* Content */}
      <div ref={subPageContentRef} className="sub-page-content flex-1 xl:min-h-0 xl:overflow-y-auto">
        <PageShell className="px-4 py-4 xl:max-w-4xl xl:mx-auto xl:py-6">
          {hideHeader && (
            <div className="flex items-center justify-between gap-3 pb-4 mb-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <Briefcase className="w-8 h-8 text-purple-600 flex-shrink-0" />
                <div>
                  <h2 className="font-semibold text-gray-900">Mine oppdrag</h2>
                  <p className="text-sm text-gray-600">{gigs.length} oppdrag</p>
                </div>
              </div>
              <button
                onClick={() => { triggerAndroidHaptic('light'); onCreateGig(); }}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all duration-150 active:scale-[0.98] font-semibold flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Nytt oppdrag
              </button>
            </div>
          )}
          {loading ? (
            <div className="text-center py-12">
              <Loader size="xl" className="text-purple-600" />
              <p className="mt-4 text-gray-700">Laster oppdrag...</p>
            </div>
          ) : gigs.length === 0 ? (
            <div className="text-center py-12 bg-white p-8 xl:rounded-2xl xl:shadow-md">
              <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-2xl mb-3 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                Ingen oppdrag ennå
              </h3>
              <p className="text-gray-600 mb-6">
                Opprett ditt første oppdrag for å finne talent til dine prosjekter.
              </p>
              <button
                onClick={() => {
                  triggerAndroidHaptic('light');
                  onCreateGig();
                }}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all duration-150 active:scale-[0.98] font-semibold inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Opprett oppdrag
              </button>
            </div>
          ) : (
            <div className="space-y-3 xl:space-y-4">
              {gigs.map((gig: any) => (
                <motion.div
                  key={gig.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 xl:shadow-md xl:border-0 hover:shadow-lg transition-shadow"
                >
                  {/* Status badge */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{gig.title}</h3>
                      <p className="text-sm text-gray-600">{gig.county}</p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        gig.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {gig.status === 'active' ? (
                        <>
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          Publisert
                        </>
                      ) : (
                        <>
                          <XCircle className="w-3 h-3 inline mr-1" />
                          Ikke publisert
                        </>
                      )}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-gray-700 mb-4 line-clamp-2">{gig.description}</p>

                  {/* Skills */}
                  {gig.skills && gig.skills.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {gig.skills.slice(0, 5).map((skill: string, idx: number) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-xs rounded-lg border border-purple-200"
                        >
                          {skill}
                        </span>
                      ))}
                      {gig.skills.length > 5 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                          +{gig.skills.length - 5}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Budget and date */}
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                    <span className="font-medium text-purple-700">
                      {gig.budget || 'Budsjett ikke oppgitt'}
                    </span>
                    <span>•</span>
                    <span>Opprettet {formatDate(gig.createdAt)}</span>
                    {gig.expiresAt && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Utløper {formatDate(gig.expiresAt)}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-6 mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-700">
                        <strong>{gig.viewCount || 0}</strong> visninger
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-purple-600" />
                      <span className="text-sm text-gray-700">
                        <strong className="text-purple-700">{gig.interestCount || 0}</strong> interesserte
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => {
                        triggerAndroidHaptic('light');
                        onViewInterests(gig.id, gig.title);
                      }}
                      className="flex-1 min-w-[140px] px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all duration-150 active:scale-[0.98] font-semibold flex items-center justify-center gap-2"
                    >
                      <Users className="w-4 h-4" />
                      Interesserte ({gig.interestCount || 0})
                    </button>
                    {gig.status === 'active' && (
                      <>
                        <button
                          onClick={() => {
                            triggerAndroidHaptic('light');
                            onEditGig(gig.id);
                          }}
                          className="flex-1 min-w-[100px] px-4 py-2.5 bg-white border-2 border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-all duration-150 active:scale-[0.98] font-semibold flex items-center justify-center gap-2"
                        >
                          <Edit2 className="w-4 h-4" />
                          Rediger
                        </button>
                        <button
                          onClick={() => {
                            triggerAndroidHaptic('medium');
                            handleUnpublishGig(gig.id);
                          }}
                          disabled={closingGigId === gig.id}
                          className="flex-1 min-w-[100px] px-4 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-150 active:scale-[0.98] font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {closingGigId === gig.id ? (
                            <>
                              <Loader size="sm" className="text-gray-400" />
                              Tar ned...
                            </>
                          ) : (
                            <>
                              <XCircle className="w-4 h-4" />
                              Avpubliser
                            </>
                          )}
                        </button>
                      </>
                    )}
                    {gig.status === 'closed' && (
                      <button
                        onClick={() => {
                          triggerAndroidHaptic('medium');
                          handlePublishGig(gig.id);
                        }}
                        disabled={publishingGigId === gig.id}
                        className="flex-1 min-w-[120px] px-4 py-2.5 bg-white border-2 border-green-600 text-green-700 rounded-lg hover:bg-green-50 transition-all duration-150 active:scale-[0.98] font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {publishingGigId === gig.id ? (
                          <>
                            <Loader size="sm" className="text-green-600" />
                            Publiserer...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Publiser igjen
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        triggerAndroidHaptic('light');
                        setConfirmDeleteGigId(gig.id);
                      }}
                      disabled={deletingGigId === gig.id}
                      className="px-4 py-2.5 bg-white border-2 border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-all duration-150 active:scale-[0.98] font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {deletingGigId === gig.id ? (
                        <Loader size="sm" className="text-red-500" />
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Slett
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </PageShell>
      </div>

      {/* Bekreft sletting */}
      {confirmDeleteGigId && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: 'var(--z-modal)' }}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Slett oppdrag?</h3>
            <p className="text-gray-600 text-sm mb-6">
              Dette kan ikke angres. Oppdraget og alle tilknyttede henvendelser slettes permanent.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  triggerAndroidHaptic('light');
                  setConfirmDeleteGigId(null);
                }}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  triggerAndroidHaptic('medium');
                  confirmDeleteGigId && handleDeleteGig(confirmDeleteGigId);
                }}
                disabled={deletingGigId !== null}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                {deletingGigId ? 'Sletter...' : 'Slett'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
